import React, { useState, useEffect } from 'react';
import { db } from '../firebase'; // Make sure this path is correct
import {
    collection,
    onSnapshot,
    doc,
    addDoc,
    updateDoc,
    query,
    where,
    orderBy,
    Timestamp,
    deleteDoc,
    getDoc,      // <-- Import getDoc to check for documents
    writeBatch   // <-- Import writeBatch for safe, atomic operations
} from 'firebase/firestore';
import { FaCheck, FaTimes, FaTrash } from 'react-icons/fa';
import './AdminPages.css'; // Make sure you have this CSS file

const HubRequests = () => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // READ: Fetch pending hub requests from Firestore
    useEffect(() => {
        // This query requires a composite index in Firestore.
        // If you get an error, check the browser console (F12) for a link to create it.
        const requestsQuery = query(
            collection(db, 'hubrequests'),    // CORRECTED: Collection name is 'hubrequests'
            where('status', '==', 'pending'),
            orderBy('request_date', 'desc')
        );

        const unsubscribe = onSnapshot(requestsQuery, 
            (snapshot) => {
                const requestsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setRequests(requestsData);
                setLoading(false);
            },
            (err) => {
                console.error("Error fetching hub requests:", err);
                setError('Failed to load requests. You may need to create a Firestore index. Check the console (F12) for a creation link.');
                setLoading(false);
            }
        );

        // Cleanup the listener when the component unmounts
        return () => unsubscribe();
    }, []); // Empty array ensures this effect runs only once on mount

    // UPDATE & CREATE: Approve a request, create a new hub, and update the user
    const handleApprove = async (request) => {
        const location = prompt(`Enter the location for the new hub: "${request.hub}"`);
        if (!location || location.trim() === "") {
            alert("A valid location is required to create a hub.");
            return;
        }

        // Use a batch write for an "all-or-nothing" operation.
        // This prevents the database from being left in an inconsistent state.
        const batch = writeBatch(db);

        try {
            // Step 1: Define the new hub and add its creation to the batch
            const newHubRef = doc(collection(db, 'hubs')); // Prepare a reference for the new hub
            batch.set(newHubRef, {
                hubName: request.hub,
                location: location.trim(),
                createdAt: Timestamp.now(),
            });

            // Step 2: Safely check for the user document before adding its update to the batch
            if (request.requestedBy_id) {
                const userDocRef = doc(db, 'users', request.requestedBy_id);
                const userDocSnap = await getDoc(userDocRef);

                if (userDocSnap.exists()) {
                    // The user exists, so we can add the update to our batch
                    batch.update(userDocRef, { hubId: newHubRef.id });
                } else {
                    // The user does not exist, log a warning but don't stop the process
                    console.warn(`User with ID "${request.requestedBy_id}" not found in 'users' collection. Skipping user update.`);
                }
            }

            // Step 3: Add the request status update to the batch
            const requestDocRef = doc(db, 'hubrequests', request.id); // CORRECTED: Collection name
            batch.update(requestDocRef, { status: 'approved' });

            // Step 4: Commit all changes to the database at once
            await batch.commit();

            alert('Hub has been successfully created and the request is approved!');

        } catch (err) {
            console.error("Error during approval process:", err);
            alert('An error occurred while approving the request. Check the console for more details.');
        }
    };
    
    // UPDATE: Reject a request by changing its status
    const handleReject = async (requestId) => {
        if (window.confirm("Are you sure you want to reject this request?")) {
            try {
                const requestDocRef = doc(db, 'hubrequests', requestId); // CORRECTED: Collection name
                await updateDoc(requestDocRef, { status: 'rejected' });
                alert('Request has been rejected.');
            } catch (err) {
                console.error("Error rejecting request:", err);
                alert('Failed to reject the request. Please try again.');
            }
        }
    };

    // DELETE: Permanently delete a request from the database
    const handleDelete = async (requestId) => {
        if (window.confirm("Are you sure you want to PERMANENTLY DELETE this request? This action cannot be undone.")) {
            try {
                await deleteDoc(doc(db, 'hubrequests', requestId)); // CORRECTED: Collection name
                alert('Request has been permanently deleted.');
            } catch (err) {
                console.error("Error deleting request:", err);
                alert('Failed to delete the request. Please try again.');
            }
        }
    };

    // --- RENDER LOGIC ---

    if (loading) {
        return <div className="admin-page-container"><h2>Loading Requests...</h2></div>;
    }

    if (error) {
        return <div className="admin-page-container"><h2>Error: {error}</h2></div>;
    }

    return (
        <div className="admin-page-container">
            <h1>New Hub Requests</h1>
            <div className="card">
                <table className="requests-table">
                    <thead>
                        <tr>
                            <th>Customer Name</th>
                            <th>Requested Hub</th>
                            <th>Request Date</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {requests.length === 0 ? (
                            <tr>
                                <td colSpan="4">No pending requests found.</td>
                            </tr>
                        ) : (
                            requests.map((req) => (
                                <tr key={req.id}>
                                    <td>{req.requestedBy_name || 'N/A'}</td>
                                    <td>{req.hub || 'N/A'}</td>
                                    {/* Safely display the timestamp */}
                                    <td>{req.request_date?.toDate().toLocaleString() || 'N/A'}</td>
                                    <td className="action-buttons">
                                        <button className="approve" onClick={() => handleApprove(req)} title="Approve and Create Hub">
                                            <FaCheck /> Approve
                                        </button>
                                        <button className="reject" onClick={() => handleReject(req.id)} title="Reject Request">
                                            <FaTimes /> Reject
                                        </button>
                                        <button className="delete" onClick={() => handleDelete(req.id)} title="Delete Request Permanently">
                                            <FaTrash /> Delete
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default HubRequests;